import { z } from 'zod';
import { createPaginatedResponseSchema, IdParamsSchema, MetadataSchema, PaginationQuerySchema, } from '../common/index.js';
export const UserSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string().min(1).max(100),
    avatar_url: z.string().url().nullable().optional(),
    email_verified: z.boolean().default(false),
    phone: z.string().max(20).nullable().optional(),
    timezone: z.string().default('UTC').optional(),
    language: z.string().default('en').optional(),
    metadata: MetadataSchema.optional(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    last_sign_in_at: z.string().datetime().nullable().optional(),
});
export const UserProfileSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string().email(),
    avatar_url: z.string().url().nullable().optional(),
    created_at: z.string().datetime(),
});
export const UserPreferencesSchema = z.object({
    theme: z.enum(['light', 'dark', 'system']).default('system'),
    notifications: z
        .object({
        email: z.boolean().default(true),
        push: z.boolean().default(true),
        in_app: z.boolean().default(true),
    })
        .optional(),
    dashboard_layout: z.enum(['grid', 'list']).default('grid').optional(),
    date_format: z.string().default('MM/DD/YYYY').optional(),
    time_format: z.enum(['12h', '24h']).default('12h').optional(),
});
export const CreateUserSchema = z.object({
    email: z.string().email('Invalid email address'),
    name: z.string().min(2, 'Name must be at least 2 characters').max(100),
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
    phone: z.string().max(20).optional(),
    timezone: z.string().default('UTC').optional(),
    language: z.string().default('en').optional(),
    metadata: MetadataSchema.optional(),
});
export const UpdateUserSchema = z.object({
    name: z.string().min(2).max(100).optional(),
    avatar_url: z.string().url().optional().nullable(),
    phone: z.string().max(20).optional().nullable(),
    timezone: z.string().optional(),
    language: z.string().optional(),
    metadata: MetadataSchema.optional(),
});
export const UpdateUserProfileResponseSchema = z.object({
    success: z.boolean(),
    user: UserProfileSchema,
    updated_at: z.string().datetime(),
});
export const UpdateUserPreferencesSchema = UserPreferencesSchema.partial();
export const UserInvitationSchema = z.object({
    id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    email: z.string().email(),
    role: z.enum(['owner', 'admin', 'member']).default('member'),
    invited_by: z.string().uuid(),
    token: z.string(),
    expires_at: z.string().datetime(),
    accepted_at: z.string().datetime().nullable().optional(),
    status: z
        .enum(['pending', 'accepted', 'expired', 'revoked'])
        .default('pending'),
    metadata: MetadataSchema.optional(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const InviteUserSchema = z.object({
    tenant_id: z.string().uuid(),
    email: z.string().email('Invalid email address'),
    role: z.enum(['owner', 'admin', 'member']).default('member'),
    message: z.string().max(500).optional(),
    metadata: MetadataSchema.optional(),
});
export const ResendInvitationSchema = z.object({
    invitation_id: z.string().uuid(),
});
export const RevokeInvitationSchema = z.object({
    invitation_id: z.string().uuid(),
    reason: z.string().max(200).optional(),
});
export const UserQuerySchema = PaginationQuerySchema.extend({
    search: z.string().optional(),
    tenant_id: z.string().uuid().optional(),
    role: z.enum(['owner', 'admin', 'member']).optional(),
    email_verified: z.boolean().optional(),
    sort: z
        .enum(['name', 'email', 'created_at', 'last_sign_in_at'])
        .default('created_at')
        .optional(),
    order: z.enum(['asc', 'desc']).default('desc').optional(),
});
export const UserInvitationQuerySchema = PaginationQuerySchema.extend({
    tenant_id: z.string().uuid().optional(),
    status: z.enum(['pending', 'accepted', 'expired', 'revoked']).optional(),
    email: z.string().email().optional(),
    sort: z
        .enum(['created_at', 'expires_at', 'email'])
        .default('created_at')
        .optional(),
    order: z.enum(['asc', 'desc']).default('desc').optional(),
});
export const UserResponseSchema = UserSchema.extend({
    preferences: UserPreferencesSchema.optional(),
});
export const UserInvitationResponseSchema = UserInvitationSchema.extend({
    inviter: UserProfileSchema.optional(),
    tenant_name: z.string().optional(),
});
export const InviteUserResponseSchema = z.object({
    invitation: UserInvitationResponseSchema,
    message: z.string(),
});
export const PaginatedUsersResponseSchema = createPaginatedResponseSchema(UserResponseSchema);
export const PaginatedUserInvitationsResponseSchema = createPaginatedResponseSchema(UserInvitationResponseSchema);
export const UserParamsSchema = IdParamsSchema;
export const UserInvitationParamsSchema = z.object({
    id: z.string().uuid(),
});
export const TeamMemberSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string().nullable(),
    role: z.enum(['admin', 'member', 'viewer']),
    status: z.enum(['active', 'inactive', 'pending']),
    avatar_url: z.string().url().nullable().optional(),
    last_login_at: z.string().datetime().nullable().optional(),
    invited_by: z
        .object({
        id: z.string(),
        name: z.string().nullable(),
        email: z.string().email(),
    })
        .nullable()
        .optional(),
    invited_at: z.string().datetime().nullable().optional(),
    joined_at: z.string().datetime().nullable().optional(),
    created_at: z.string().datetime(),
});
export const PendingInvitationSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    role: z.enum(['admin', 'member', 'viewer']),
    status: z.enum(['pending', 'accepted', 'rejected', 'expired']),
    expires_at: z.string().datetime(),
    invited_by: z.object({
        id: z.string(),
        name: z.string().nullable(),
        email: z.string().email(),
    }),
    message: z.string().nullable().optional(),
    created_at: z.string().datetime(),
});
export const TeamMembersResponseSchema = z.object({
    users: z.array(TeamMemberSchema),
    members: z.array(TeamMemberSchema),
    pending_invitations: z.array(PendingInvitationSchema),
    total_members: z.number(),
    total_pending: z.number(),
});
export const UpdateUserRoleSchema = z.object({
    role: z.enum(['admin', 'member', 'viewer']),
});
export const UpdateUserRoleResponseSchema = z.object({
    success: z.boolean(),
    user: TeamMemberSchema,
    updated_at: z.string().datetime(),
});
export const RemoveUserResponseSchema = z.object({
    success: z.boolean(),
    removed_at: z.string().datetime(),
});
export const RejectInvitationSchema = z.object({
    invitation_token: z.string().min(1, 'Invitation token is required'),
});
export const RejectInvitationResponseSchema = z.object({
    success: z.boolean(),
    rejected_at: z.string().datetime(),
});
//# sourceMappingURL=index.js.map